import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CheckCircle, ArrowRight, Phone, Home, Sun, Clock, Euro, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import ContactModal from "../components/ContactModal";
import Seo from "../components/Seo";
import { serviceJsonLd, filArianeJsonLd } from "../utils/donneesStructurees";
import GuidesLies from "../components/GuidesLies";
import LienAidesComplementaires from "../components/LienAidesComplementaires";

const Exterieurs = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const benefits = [
    "Accès sécurisé à votre domicile",
    "Prévention des chutes sur les seuils", 
    "Éclairage automatique pour la sécurité",
    "Revêtements antidérapants durables",
    "Adaptation aux conditions météorologiques",
    "Amélioration de l'autonomie d'accès"
  ];

  const solutions = [
    {
      title: "Rampe d'accès extérieure",
      description: "Rampe fixe ou modulaire pour franchir les marches d'entrée",
      price: "4 500 - 12 000€",
      duration: "1-2 jours"
    },
    {
      title: "Éclairage sécurisé", 
      description: "Éclairage LED automatique avec détection de mouvement",
      price: "2 000 - 6 000€",
      duration: "1 jour"
    },
    {
      title: "Aménagement global",
      description: "Cheminements, signalisation, main courantes extérieures",
      price: "8 000 - 18 000€",
      duration: "2-5 jours"
    }
  ];

  const amenagements = [
    "Rampes d'accès fixes et amovibles",
    "Revêtements antidérapants", 
    "Éclairage LED à détection automatique",
    "Main courantes et barres d'appui extérieures",
    "Adaptation des seuils de portes",
    "Signalisation tactile et visuelle",
    "Protection contre les intempéries",
    "Cheminements sécurisés"
  ];

  const process = [
    {
      step: "1",
      title: "Analyse de l'accès",
      description: "Diagnostic des obstacles et dangers sur les cheminements d'accès à votre domicile"
    },
    {
      step: "2", 
      title: "Solutions techniques",
      description: "Proposition d'aménagements adaptés aux contraintes du terrain et réglementation"
    },
    {
      step: "3",
      title: "Financement MaPrimeAdapt'",
      description: "Montage du dossier d'aide avec calcul des subventions disponibles"
    },
    {
      step: "4",
      title: "Réalisation des travaux",
      description: "Installation par nos équipes spécialisées avec respect des normes de sécurité"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre="Aménagement extérieur MaPrimeAdapt' | Rampes, allées, accès PMR | TEF IDF"
        description="Financement jusqu'à 70 % pour vos aménagements extérieurs : rampes d'accès, allées sécurisées, portails automatiques. AMO agréé ANAH en Île-de-France."
        chemin="/exterieurs"
        donnees={[
          serviceJsonLd({
            nom: "Adaptation des accès et des extérieurs",
            description: "Rampe d'accès, cheminement stabilisé, suppression des seuils et sécurisation des abords du logement.",
            url: "/exterieurs",
          }),
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "Extérieurs adaptés", url: "/exterieurs" },
          ]),
        ]}
      />
      <Header />

      <main id="contenu">
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-gray-50 to-white py-20">
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center px-4 py-2 bg-orange-700 text-white rounded-full text-sm font-medium">
                  <Home className="h-4 w-4 mr-2" />
                  Aménagements extérieurs • MaPrimeAdapt'
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  Aménagements Extérieurs PMR : Rampes et Éclairage{" "}
                  <span className="text-orange-700 block mt-2">Sécurisé MaPrimeAdapt' 2026</span>
                </h1>
                
                <p className="text-xl text-gray-900 leading-relaxed">
                  <strong>Expert aménagements extérieurs PMR France</strong> : rampes d'accès conformes, éclairage sécurisé LED, 
                  cheminements antidérapants selon normes accessibilité. AMO habilité ANAH, dossier MaPrimeAdapt' complet 
                  avec artisans certifiés. <strong className="text-orange-700">Jusqu'à 15 400€ d'aides financières</strong> selon revenus, pour des travaux plafonnés à 22 000€.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/simulateur">
                  <Button size="lg" className="bg-orange-700 hover:bg-orange-800 text-white shadow-lg group w-full sm:w-auto">
                    Tester mon éligibilité
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-orange-200 hover:bg-orange-50 w-full sm:w-auto"
                onClick={() => setIsContactModalOpen(true)}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Nous contacter
              </Button>
              
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-orange-200 hover:bg-orange-50 w-full sm:w-auto"
                  onClick={() => window.location.href = 'tel:0939035444'}
                >
                  <Phone className="mr-2 h-5 w-5" />
                  09 39 03 54 44
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-6 pt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-700">1-5 jours</div>
                  <div className="text-sm text-gray-700">Installation</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-700">Toute saison</div>
                  <div className="text-sm text-gray-700">Utilisation</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-700">10 ans</div>
                  <div className="text-sm text-gray-700">Garantie</div>
                </div>
              </div>
            </div>

            {/* Example Box avec image */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
              <img 
                src="/images/rampe-acces.webp"
                width="1280"
                height="960"
                loading="eager" 
                alt="Aménagements extérieurs PMR - Rampe d'accès avec éclairage" 
                className="w-full h-64 object-cover"
              />
              <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                Exemple : Rampe d'accès + éclairage
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-700">Coût total des travaux</span>
                  <span className="font-semibold text-gray-900">16 000 €</span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-green-700">MaPrimeAdapt' (70%)</span>
                    <span className="font-semibold text-green-700">- 11 200 €</span>
                  </div>
                </div>

                <div className="pt-4 border-t-2 border-orange-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Reste à charge</span>
                    <span className="text-2xl font-bold text-gray-400 line-through decoration-2 decoration-orange-400">4 800 €</span>
                  </div>
                </div>
                <LienAidesComplementaires />
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Section des adaptations extérieures principales */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Dans le reste du logement : Autres Aménagements PMR MaPrimeAdapt' 2026
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              <strong>De nombreux autres travaux d'adaptation du logement sont pris en charge par MaPrimeAdapt'</strong> : 
              volets roulants électriques, élargissement portes, création unité de vie PMR, cheminements extérieurs sécurisés. 
              Installation par artisans certifiés avec dossier complet jusqu'à 15 400€ d'aides.
            </p>
          </div>

          {/* Section explicative aménagements extérieurs PMR */}
          <div className="mb-16">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-12">
              <h3 className="text-xl font-semibold text-orange-800 mb-4">
                Autres Adaptations Logement PMR : Volets, Portes et Unités de Vie 2026
              </h3>
              <p className="text-orange-700 leading-relaxed mb-4">
                <strong>MaPrimeAdapt'</strong> finance de nombreuses adaptations complémentaires pour l'autonomie des seniors et personnes handicapées. 
                Volets roulants électriques (confort quotidien), élargissement portes selon normes PMR, création unité de vie accessible, 
                cheminements extérieurs sécurisés conformes réglementation française accessibilité.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div>
                  <h4 className="font-semibold text-orange-800 mb-3">🔧 Normes techniques extérieures :</h4>
                  <ul className="text-sm text-orange-700 space-y-2">
                    <li>• <strong>Rampes :</strong> Pente max 5%, largeur 120 cm min</li>
                    <li>• <strong>Éclairage :</strong> 20 lux minimum, LED obligatoire</li>
                    <li>• <strong>Cheminements :</strong> 140 cm largeur, dévers 2% max</li>
                    <li>• <strong>Revêtements :</strong> R11 antidérapant certifié</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-orange-800 mb-3">✅ Certifications obligatoires :</h4>
                  <ul className="text-sm text-orange-700 space-y-2">
                    <li>• Matériaux marquage CE conformes</li>
                    <li>• Éclairage LED étanchéité IP44 minimum</li>
                    <li>• Installation par artisans RGE qualifiés</li>
                    <li>• Contrôle conformité réception travaux</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Grid 2x2 des aménagements selon votre modèle */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Volets roulants électriques - TRÈS IMPORTANT */}
              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <div className="flex items-start space-x-6">
                  <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-3 shadow-md">
                    <img 
                      src="/images/volets-automatiques.webp"
                      width="1024"
                      height="683"
                      loading="lazy" 
                      alt="Volets roulants électriques PMR" 
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-red-700 mb-3">
                      Volets roulants électriques
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed mb-3">
                      Installer des <strong>volets roulants électriques</strong> permet de simplifier le quotidien des personnes âgées. 
                      Elles gagnent ainsi en confort et limitent le risque de chute. Motorisation silencieuse, 
                      télécommande, programmation automatique pour sécurité renforcée jour/nuit.
                    </p>
                    <div className="bg-red-100 p-3 rounded text-xs text-red-800">
                      <strong>Prix :</strong> 400-1 200€/volet • <strong>Installation :</strong> 1 jour • 
                      <strong>Garantie :</strong> 5 ans moteur
                    </div>
                  </div>
                </div>
              </div>

              {/* Élargissement de portes */}
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-start space-x-6">
                  <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-3 shadow-md">
                    <img 
                      src="/images/wc-pmr.webp"
                      width="660"
                      height="660"
                      loading="lazy" 
                      alt="Élargissement de portes PMR" 
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-blue-700 mb-3">
                      Élargissement de portes
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed mb-3">
                      Pour les personnes en <strong>fauteuil roulant</strong>, il est indispensable d'adapter le logement aux <strong>normes PMR</strong>. 
                      L'élargissement de portes permet aux personnes handicapées de gagner en <strong>accessibilité</strong> 
                      dans l'ensemble du logement. Largeur libre 80 cm minimum selon réglementation.
                    </p>
                    <div className="bg-blue-100 p-3 rounded text-xs text-blue-800">
                      <strong>Prix :</strong> 800-2 500€/porte • <strong>Largeur :</strong> 80 cm libre • 
                      <strong>Normes :</strong> PMR certifiées
                    </div>
                  </div>
                </div>
              </div>

              {/* Pièce ou unité de vie PMR */}
              <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                <div className="flex items-start space-x-6">
                  <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-3 shadow-md">
                    <img 
                      src="/images/eclairage.webp"
                      width="650"
                      height="500"
                      loading="lazy" 
                      alt="Aménagement extérieur PMR rampe accès" 
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-green-700 mb-3">
                      Aménagement extérieur complet
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed mb-3">
                      Aménagement complet des <strong>accès extérieurs PMR</strong> : rampes d'accès sécurisées, 
                      cheminements éclairés, main-courantes, suppression des obstacles. 
                      Solutions sur-mesure pour <strong>accessibilité totale</strong> du domicile selon normes DTU.
                    </p>
                    <div className="bg-green-100 p-3 rounded text-xs text-green-800">
                      <strong>Prix :</strong> 3 000-12 000€ • <strong>Aménagement :</strong> Sur mesure • 
                      <strong>MaPrimeAdapt' :</strong> 70% pris en charge
                    </div>
                  </div>
                </div>
              </div>

              {/* Cheminement extérieur */}
              <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                <div className="flex items-start space-x-6">
                  <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center flex-shrink-0 p-3 shadow-md">
                    <img 
                      src="/images/automatismes.webp"
                      width="695"
                      height="395"
                      loading="lazy" 
                      alt="Cheminement extérieur PMR" 
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-purple-700 mb-3">
                      Cheminement extérieur
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed mb-3">
                      Parfois l'accès au logement est compliqué pour les seniors en perte d'autonomie. MaPrimeAdapt' 
                      finance les travaux pour réaliser un <strong>cheminement extérieur</strong> et vous permettre de conserver un 
                      <strong>accès sécurisé</strong> à votre domicile. Largeur 140 cm, pente 5% maximum.
                    </p>
                    <div className="bg-purple-100 p-3 rounded text-xs text-purple-800">
                      <strong>Prix :</strong> 50-120€/m² • <strong>Largeur :</strong> 140 cm min • 
                      <strong>Pente :</strong> 5% max DTU
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Prix Aménagements Extérieurs PMR avec MaPrimeAdapt' 2026
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              <strong>Tarifs équipements extérieurs PMR France</strong> : rampes d'accès certifiées, éclairage LED sécurisé, 
              cheminements conformes DTU. Installation artisans RGE avec garanties, dossier MaPrimeAdapt' 
              jusqu'à 15 400€ d'aides selon les plafonds de revenus ANAH en vigueur.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {solutions.map((solution, index) => (
              <Card key={index} className="border-2 hover:border-orange-200 transition-all duration-300 hover:shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900 mb-2">
                    {solution.title}
                  </CardTitle>
                  <CardDescription className="text-gray-700">
                    {solution.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <Euro className="h-3 w-3 mr-1" />
                      {solution.price}
                    </Badge>
                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                      <Clock className="h-3 w-3 mr-1" />
                      {solution.duration}
                    </Badge>
                  </div>
                  
                  <Link to="/simulateur">
                    <Button className="w-full bg-orange-700 hover:bg-orange-800">
                      Obtenir un devis
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Amenagements List */}
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Types d'aménagements extérieurs
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {amenagements.map((amenagement, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700 text-sm">{amenagement}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Pourquoi aménager vos extérieurs ?
              </h2>
              
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-orange-700" />
                    </div>
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-orange-50 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                Aménagements adaptés aux saisons
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Sun className="h-6 w-6 text-orange-700" />
                  <span className="text-gray-700">Résistance aux UV et intempéries</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Home className="h-6 w-6 text-orange-700" />
                  <span className="text-gray-700">Matériaux antidérapants même humides</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-orange-700" />
                  <span className="text-gray-700">Maintenance réduite et durabilité</span>
                </div>
              </div>
              
              <Link to="/simulateur" className="mt-6 block">
                <Button className="w-full bg-orange-700 hover:bg-orange-800">
                  Évaluer mes besoins
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Notre processus d'aménagement
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Une approche méthodique pour des aménagements extérieurs sûrs et durables
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-orange-700 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-orange-200 transform -translate-y-1/2"></div>
                  )}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h4>
                <p className="text-gray-700 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-orange-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">
            Sécurisez vos accès extérieurs
          </h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Facilitez vos déplacements en toute saison avec nos aménagements sur-mesure
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
            <Link to="/simulateur" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                className="bg-white text-orange-700 hover:bg-gray-100 w-full sm:w-auto shadow-lg"
              >
                Simulation gratuite
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 w-full sm:w-auto"
              onClick={() => setIsContactModalOpen(true)}
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Nous contacter
            </Button>
            
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-orange-700 w-full sm:w-auto"
              onClick={() => window.location.href = 'tel:0939035444'}
            >
              <Phone className="mr-2 h-5 w-5" />
              09 39 03 54 44
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section optimisée SEO */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Questions Fréquentes : Aménagements Extérieurs PMR et MaPrimeAdapt' 2026
            </h2>
            
            <div className="space-y-8">
              <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Quel est le prix d'une rampe d'accès extérieure PMR avec MaPrimeAdapt' ?
                </h3>
                <p className="text-gray-700">
                  Prix rampe d'accès extérieure PMR : <strong>2 000€ à 8 000€</strong> selon longueur et matériaux (aluminium/bois/béton). 
                  Avec MaPrimeAdapt', prise en charge jusqu'à <strong>70% du coût</strong> (soit 15 400€ d'aide maximum, pour des travaux plafonnés à 22 000€). 
                  Installation conforme DTU 36.1 par artisans RGE, garantie décennale incluse.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Quelles normes respecter pour éclairage extérieur PMR domicile ?
                </h3>
                <p className="text-gray-700">
                  <strong>Normes éclairage extérieur PMR</strong> : éclairement minimum 20 lux selon NF C 17-200, 
                  étanchéité IP44 obligatoire, LED avec détection mouvement. Alimentation sécurisée 12V recommandée, 
                  installation par électricien qualifié, contrôle CONSUEL si création circuit.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Comment financer aménagements extérieurs PMR avec aides publiques ?
                </h3>
                <p className="text-gray-700">
                  <strong>Financement aménagements extérieurs PMR</strong> : MaPrimeAdapt' jusqu'à 15 400€ d'aides (70% de travaux plafonnés à 22 000€), 
                  MDPH PCH aide technique, caisses retraite CARSAT/MSA, collectivités territoriales. 
                  Éligibilité : 70 ans et plus ou GIR 1-6, revenus modestes, résidence principale uniquement.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Quels délais installation équipements extérieurs PMR certifiés ?
                </h3>
                <p className="text-gray-700">
                  <strong>Délais aménagements extérieurs PMR</strong> : rampe d'accès 1-2 jours installation, 
                  éclairage sécurisé 1 jour, cheminements 2-5 jours selon surface. Dossier MaPrimeAdapt' 
                  traité 2-4 mois ANAH. Maintenance préventive annuelle recommandée, garanties constructeur 5-10 ans.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <GuidesLies slugs={["comment-obtenir-maprimeadapt", "reste-a-charge-adaptation-logement"]} />

      </main>

      <Footer />
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        type="general"
      />

    </div>
  );
};

export default Exterieurs;